/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   08/11/2022
 */

import React from "react";
import {
	Box,
	Checkbox,
	Chip,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select
} from "@mui/material";
import { UseFormReturnType } from "@mantine/form";
import { CalendarSettingsForm } from "./CalendarSettingsDialog";
import { CalendarSettings, EmployeesGroupBySetting } from "../interfaces/Global";
import TypeaheadControl from "../controls/TypeaheadControl";
import { EmployeeTagsQuery } from "../graphql/queries/employees";
import { eventTypes, getEventType } from "./event-types";

export interface CalendarSettingsDisplayProps {
	form: UseFormReturnType<CalendarSettingsForm>;
}

const groupByOptions: Record<EmployeesGroupBySetting, string> = {
	off: "No grouping",
	manager: "Manager",
	location: "Location",
	company: "Product",
	department: "Department",
	division: "Division",
	jobTitle: "Job Title",
	country: "Country",
	tags: "Custom Tags",
};

export default function CalendarSettingsDisplay({ form }: CalendarSettingsDisplayProps) {

	return (
		<Grid container spacing={2}>
			<Grid item xs={4}>
				<FormControl variant={"standard"} fullWidth>
					<InputLabel>Group Employees By</InputLabel>
					<Select
						{...form.getInputProps("settings.groupBy")}
						onChange={(event) => form.setFieldValue("settings.groupBy", event.target.value)}
					>
						{Object.keys(groupByOptions).map(gb =>
							<MenuItem key={gb} value={gb}>{groupByOptions[gb as EmployeesGroupBySetting]}</MenuItem>)
						}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={8}>
				{form.values.settings.groupBy === "tags" &&
					<FormControl variant={"standard"} fullWidth>
						<TypeaheadControl
							label={"Include Tags"}
							free
							values={form.values.settings.filterTags || []}
							onChange={(values) => form.setFieldValue("settings.filterTags", values)}
							query={EmployeeTagsQuery}
						/>
					</FormControl>}
			</Grid>
			<Grid item xs={6}>
				<FormControl variant={"standard"} fullWidth disabled={form.values.settings.groupBy === "off"}>
					<FormControlLabel
						control={
							<Checkbox
								{...form.getInputProps("settings.groupHeaderEnabled", { type: "checkbox" })}
							/>
						}
						label={"Show date headers in groups"}
					/>
					<FormHelperText>
						When enabled, header with dates will be shown in each group when applicable.
					</FormHelperText>
				</FormControl>
			</Grid>
			<Grid item xs={6}>
				<FormControl variant={"standard"} fullWidth>
					<FormControlLabel
						control={
							<Checkbox
								{...form.getInputProps("settings.showBottomHeader", { type: "checkbox" })}
							/>
						}
						label={"Show additional dates header at the bottom"}
					/>
					<FormHelperText>
						When enabled, header with dates will be shown at the bottom of the view or each group.
					</FormHelperText>
				</FormControl>
			</Grid>
			<Grid item xs={6}>
				<FormControl variant={"standard"} fullWidth>
					<InputLabel>Default Calendar View</InputLabel>
					<Select
						{...form.getInputProps("settings.defaultView")}
						onChange={(event) => form.setFieldValue("settings.defaultView", event.target.value)}
					>
						<MenuItem value={"month"}>Month</MenuItem>
						<MenuItem value={"day"}>Day</MenuItem>
					</Select>
					<FormHelperText>This calendar view will open by default when loading this calendar.</FormHelperText>
				</FormControl>
			</Grid>
			<Grid item xs={6}>
				<FormControl variant={"standard"} fullWidth>
					<InputLabel>Minimum events duration in Month Calendar</InputLabel>
					<Select
						{...form.getInputProps("settings.monthEventMinDurationHours")}
						onChange={(event) => form.setFieldValue("settings.monthEventMinDurationHours", event.target.value)}
					>
						{[ 0, 1, 2, 3, 4, 6, 8, 12, 24 ].map(i => <MenuItem key={i}
						                                                    value={i}>{i ? "At least " + i + " hours" : "Show everything"}</MenuItem>)}
					</Select>
					<FormHelperText>Events with duration shorter than specified will be hidden from Month calendar
						view.</FormHelperText>
				</FormControl>
			</Grid>
			<Grid item xs={12}>
				<FormControl variant={"standard"} fullWidth>
					<InputLabel>Show Event Categories</InputLabel>
					<Select<string[]>
						multiple
						{...form.getInputProps("settings.displayEventTypes")}
						value={form.values.settings.displayEventTypes.length > 0 ? form.values.settings.displayEventTypes : eventTypes.filter(e => !e.parentId).map(e => e.id)}
						onChange={(event) => form.setFieldValue("settings.displayEventTypes", event.target.value)}
						renderValue={(selected) => (
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: .5 }}>
								{selected.map((value) => (
									<Chip key={value} label={getEventType(value)?.name || value}/>
								))}
							</Box>
						)}
					>
						{eventTypes.filter(e => !e.parentId).map(e =>
							<MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)
						}
					</Select>
				</FormControl>
			</Grid>
			{Object.entries({
				displayVegaEvents: "Display Vega Events",
				displayGoogleEvents: "Display Google Events",
				displayWorkdayEvents: "Display Workday Events",
				displayOpsGenieEvents: "Display OpsGenie Events",
				displayOooEvents: "Display Out Of Office Events",
				displayOverlapEvents: "Display Overlap Events",
				displayHolidays: "Display Holidays",
				displayEmployeeBirthdays: "Display Employee Birthdays",
				displayEmployeeAnniversaries: "Display Employee Anniversaries",
				showCountryFlags: "Display Employee Country Flags"
			} as Record<keyof CalendarSettings, string>).map(([ setting, label ]) =>
				<Grid item xs={4} key={setting}>
					<FormControl variant={"standard"} fullWidth>
						<FormControlLabel
							control={
								<Checkbox
									{...form.getInputProps("settings." + setting, { type: "checkbox" })}
								/>
							}
							label={label}
						/>
					</FormControl>
				</Grid>
			)}
		</Grid>
	);

}